// The website-template contract (ARCHITECTURE §4.2, §4.5).
//
// A template is two modules:
//   - `templates/<id>/manifest.ts`: eager and tiny (SiteTemplateManifest).
//   - `templates/<id>/template.tsx`: the SiteTemplate itself, loaded lazily
//     through `manifest.load` (every template except classic, which is eager
//     through `manifest.module`, D10).
//
// Mapped types make every page, home-section type, shell slot and state slot
// required: adding a page key or section type fails `tsc` for every template
// until it implements the new slot.

import type { ComponentType, ReactNode } from 'react'
import type { OptionsOf, OptionsSchema } from './options'
import type { PageStatusKind, PageVMMap, SitePageKey } from './pages'
import type { HomeSectionViewMap } from '../pages/home/types'
import type { SiteBrand } from './brand'
import type { TemplateText } from './text'

export type { SitePageKey, PageVMMap } from './pages'

export const SITE_TEMPLATE_CONTRACT = 1 as const
export const SITE_TEMPLATE_ID_PATTERN = /^[a-z][a-z0-9-]{1,31}$/

export interface SiteTemplateCapabilities {
  /** Honours left/right/full column hints (the admin shows a note when false). */
  homeColumns: boolean
  /** Honours mobile_display_order. */
  homeMobileOrder: boolean
}

/** Classic-only escape hatch: a known gap kept for pixel parity, tied to an issue. */
export interface LegacyExemption {
  rule: 'footer' | 'header' | 'not-found' | 'feature'
  pages?: SitePageKey[]
  /** Feature ids exempted (rule 'feature'). */
  features?: string[]
  /** Limit to these VM states. */
  status?: PageStatusKind[]
  /** '#312'; required. */
  issue: string
}

export interface SiteTemplateManifest<S extends OptionsSchema = OptionsSchema> {
  /** ^[a-z][a-z0-9-]{1,31}$ and equal to the folder name. Never changes once shipped. */
  id: string
  /** "Arena" */
  name: string
  /** One line for the admin picker (plain text or an admin-catalogue selector, ./text.ts). */
  description: TemplateText
  /** Optional "Best for: …" line on the admin picker card. */
  bestFor?: TemplateText
  status: 'stable' | 'beta'
  contract: typeof SITE_TEMPLATE_CONTRACT
  /** Static import URL (import thumb from './thumbnail.webp'); '' for classic until it has one. */
  thumbnail: string
  /** Must deep-equal the backend catalog entry (§8.3). */
  options: S
  /** Google Fonts css2 hrefs, loaded on activation; ≤ 2 families. */
  fonts?: string[]
  capabilities: SiteTemplateCapabilities
  legacyExemptions?: LegacyExemption[]
  /**
   * Initial data-affecting state core controllers seed from. Absent values
   * use the new-template default: player / team / stats sections start on
   * the league's public season (publicSeasonId). Classic sets
   * `statsScope: 'lifetime'` to keep today's behaviour.
   */
  pageDefaults?: PageDefaults
  /** Exactly one of `module` (eager; classic only, D10) or `load` (lazy; every other template). */
  module?: SiteTemplate<S>
  load?: () => Promise<{ default: SiteTemplate<S> }>
}

export interface PageDefaults {
  /** Default 'season'. */
  statsScope?: 'lifetime' | 'season'
  /**
   * #583: what Home shows when the sport has 2+ live divisions. 'follow'
   * (default): the fan's division pick (the template renders a Home
   * switcher). 'all': always All divisions, for a template with no switcher
   * on Home (Classic), so a pick made on Schedule can't silently narrow Home.
   */
  homeDivision?: HomeDivisionMode
}

export type HomeDivisionMode = 'follow' | 'all'

/** The effective Home division mode for a manifest (no host = 'follow'). */
export function defaultHomeDivision(manifest: Pick<SiteTemplateManifest, 'pageDefaults'> | null | undefined): HomeDivisionMode {
  return manifest?.pageDefaults?.homeDivision ?? 'follow'
}

/** The effective stats scope for a manifest (null manifest = no host, e.g. tests: today's lifetime). */
export function defaultStatsScope(manifest: Pick<SiteTemplateManifest, 'pageDefaults'> | null | undefined): 'lifetime' | 'season' {
  if (!manifest) return 'lifetime'
  return manifest.pageDefaults?.statsScope ?? 'season'
}

export type PageView<K extends SitePageKey> = ComponentType<{ vm: PageVMMap[K] }>
export type PageLoader<K extends SitePageKey> = () => Promise<{ default: PageView<K> }>
/** `eager`: rendered with no Suspense boundary (classic's 4 eager pages); `load`: React.lazy. */
export type PageEntry<K extends SitePageKey> = { eager: PageView<K> } | { load: PageLoader<K> }
export type PageEntries = { [K in SitePageKey]: PageEntry<K> }

/** Classic Home uses `overlay` (Navigation noSpacer: transparent bar over the hero). */
export interface HeaderProps { variant?: 'default' | 'overlay' }
/** No props today; reserved so templates can be given footer variants later. */
export type FooterProps = Record<string, never>

/** Which chrome a page renders in one status (§4.5). */
export type ChromeSpec = { header: 'overlay' | 'spaced' | 'none'; footer: boolean }
/** Per page, per status; `ready` is required, other statuses default to `ready`'s spec. */
export type ChromeMatrix = {
  [K in SitePageKey]: Partial<Record<PageStatusKind, ChromeSpec>> & { ready: ChromeSpec }
}

export interface SiteTemplateShell {
  /** Reads useSiteNav(); MUST render sf('site.header'). */
  Header: ComponentType<HeaderProps>
  /** Reads useSiteFooter(); MUST render sf('site.footer') (and 'site.footer.powered-by' when poweredBy). */
  Footer: ComponentType<FooterProps>
  /**
   * Optional per-page wrapper new templates use to place Header/Footer once.
   * PageRoute renders it OUTSIDE the ErrorBoundary and Suspense
   * (Frame > ErrorBoundary > Suspense > page), so the chrome stays put while
   * a page chunk loads (RouteLoading shows inside it) and PageError renders
   * inside it. The catch-all NotFound is rendered inside it with
   * page 'notFound'. Classic has no Frame (its pages mount their own chrome).
   */
  Frame?: ComponentType<{ page: SitePageKey | 'notFound'; children: ReactNode }>
}

export interface SiteTemplateStates {
  /** Suspense fallback while a page chunk loads. */
  RouteLoading: ComponentType
  /** ErrorBoundary fallback. */
  PageError: ComponentType<{ error: Error; reset: () => void }>
  /** The catch-all `*` route (#312). */
  NotFound: ComponentType
}

export interface SiteTemplate<S extends OptionsSchema = OptionsSchema> {
  id: string
  /**
   * Map core brand tokens + this template's options to CSS custom properties
   * set on <html>. (Method syntax on purpose: it keeps SiteTemplate<S>
   * assignable to SiteTemplate<OptionsSchema> for the registry.)
   */
  brand(brand: SiteBrand, options: OptionsOf<S>): Record<`--st-${string}`, string>
  shell: SiteTemplateShell
  states: SiteTemplateStates
  /** Every PAGE_KEYS entry is required: compile error if one is missing. */
  pages: PageEntries
  /** Static description of which chrome each page renders, per status. Drives the contract test. */
  chrome: ChromeMatrix
  /** All 9 section types required. */
  homeSections: () => Promise<{ default: HomeSectionViewMap }>
}

/** The chrome every new template declares: header + footer on every page and status. */
export function fullChrome(): ChromeMatrix {
  const spec: ChromeSpec = { header: 'spaced', footer: true }
  return {
    home: { ready: spec }, schedule: { ready: spec }, stats: { ready: spec }, game: { ready: spec },
    players: { ready: spec }, player: { ready: spec }, teams: { ready: spec }, team: { ready: spec },
    blog: { ready: spec }, page: { ready: spec }, gallery: { ready: spec }, sponsor: { ready: spec },
    venues: { ready: spec }, venue: { ready: spec }, liveEvents: { ready: spec }, liveEvent: { ready: spec },
    tickets: { ready: spec }, sale: { ready: spec }, signIn: { ready: spec },
    registrations: { ready: spec }, registration: { ready: spec },
    account: { ready: spec }, forgotPassword: { ready: spec }, resetPassword: { ready: spec },
    join: { ready: spec },
    // The private site's wall (registration step 33): drawn without the
    // template Frame, because the Frame's chrome reads are gated.
    membersOnly: { ready: { header: 'none', footer: false } },
  }
}

/** Resolve a matrix cell (statuses without their own spec use `ready`'s). */
export function chromeFor(matrix: ChromeMatrix, page: SitePageKey, status: PageStatusKind): ChromeSpec {
  return matrix[page][status] ?? matrix[page].ready
}
